'use client'

import { useCallback } from 'react'
import { useTranslations } from 'next-intl'

import { NotificationCard } from './components/NotificationCard'
import { NotificationDrawerFilters } from './components/NotificationDrawerFilters'
import { NotificationDrawerHeader } from './components/NotificationDrawerHeader'
import { useNotificationDrawerFilters } from './hooks/useNotificationDrawerFilters'
import type { NotificationDrawerProps } from './NotificationDrawer.types'
import { useNotificationsStore } from './stores/notifications.store'
import { getUnreadNotificationCount } from './utils/getUnreadNotificationCount'

import styles from './NotificationDrawer.module.scss'

export function NotificationDrawer({ onClose, onSettingsClick }: NotificationDrawerProps) {
  const t = useTranslations('notifications')
  const notifications = useNotificationsStore((state) => state.notifications)
  const markAsRead = useNotificationsStore((state) => state.markAsRead)
  const markAllAsRead = useNotificationsStore((state) => state.markAllAsRead)
  const dismissNotification = useNotificationsStore((state) => state.dismissNotification)
  const clearAll = useNotificationsStore((state) => state.clearAll)

  const unreadCount = getUnreadNotificationCount(notifications)
  const { filter, setFilter, filteredNotifications } = useNotificationDrawerFilters({ notifications })

  const handleAction = useCallback(
    (notificationId: string, actionId: string) => {
      markAsRead(notificationId)

      if (actionId === 'decline') {
        dismissNotification(notificationId)
      }
    },
    [dismissNotification, markAsRead]
  )

  const handleClearAll = useCallback(() => {
    clearAll()
    onClose()
  }, [clearAll, onClose])

  return (
    <div className={styles.drawer}>
      <NotificationDrawerHeader onClose={onClose} onSettingsClick={onSettingsClick} onClearAll={handleClearAll} />

      <NotificationDrawerFilters
        filter={filter}
        unreadCount={unreadCount}
        hasUnread={unreadCount > 0}
        onFilterChange={setFilter}
        onMarkAllAsRead={markAllAsRead}
      />

      <div className={styles.list}>
        <div className={styles.listInner}>
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification) => (
              <NotificationCard key={notification.id} notification={notification} onAction={handleAction} />
            ))
          ) : (
            <p className={styles.empty}>{t('empty')}</p>
          )}
        </div>
      </div>
    </div>
  )
}
